import { RawMaterialWarehouse } from '../raw-material-inventory/raw-material.model';

export enum ProductionTab {
  WAREHOUSES = 'warehouses',
  LABOURS = 'labours',
  LABOURS_DAILY_ATTENDANCE = 'labours-daily-attendance',
  INVENTORY = 'inventory',
  PRODUCTION_ENTRIES = 'production-entries',
  COMPANIES = 'companies',
  FACTORIES = 'factories',
  FACTORY_STOCK = 'factory-stock',
  PRODUCT_TRANSFERS = 'product-transfers',
  PRODUCTION_STOCK = 'production-stock',
  FACTORY_STOCK_ADJUSTMENTS = 'factory-stock-adjustments',
  WAREHOUSE_STOCK_ADJUSTMENTS = 'warehouse-stock-adjustments',
}

export interface Labour {
  id: string;
  warehouse: RawMaterialWarehouse;
  created_at: string; // ISO date-time
  updated_at: string; // ISO date-time
  full_name: string;
  aadhar_number: string;
  date_of_birth: string; // format: YYYY-MM-DD
  gender: 'Male' | 'Female' | 'Other';
  father_or_spouse_name: string;
  mobile_number: string;
  alternate_number: string | null;
  address: string;
  type_of_labour: 'Skilled' | 'Unskilled' | 'Supervisor';
  department: 'Mixing' | 'Packing' | 'Loading' | 'Other';
  date_of_joining: string; // format: YYYY-MM-DD
  photo_id: string; // URL
  labour_status: 'Active' | 'Inactive';
  daily_wages: number | null;
  working_hours: number | null;
  per_hour_rate: number | null;
  ot_rate: number | null;
  ta_per_day: number | null;
}

export interface LabourBasicInfo {
  id: string;
  full_name: string;
}

export interface LabourCheckIn {
  id: string;
  labour: Labour;
  created_at: string;
  updated_at: string;
  date: string;
  start_time: string;
  end_time: string | null;
  normal_hourly_rate: number | null;
  ot_hourly_rate: number | null;
  normal_hours: string | null;
  ot_hours: string;
  normal_wage: number | null;
  ot_wage: number | null;
  advance_given: string;
  advance_deducted: string;
  travel_allowance: string;
  net_payable: number | null;
  created_by: string;
}

export interface LabourCheckInResponse {
  checked_in: LabourCheckIn[];
  already_checked_in: LabourCheckIn[];
}

export interface LabourCheckInRequest {
  labour_ids: string[];
  start_time: string; // ISO date-time
}

export interface LabourCheckOutRequest {
  labour_ids: string[];
  end_time: string;
}

export interface LabourCheckOutError {
  labour_id: string;
  detail: string;
}

export interface LabourCheckOutResponse {
  checked_out: LabourCheckIn[];
  errors: LabourCheckOutError[];
}

export interface LabourAdvance {
  id: string;
  labour: string;
  amount: string;
  date: string;
  note: string;
  created_by: any;
  created_at: string;
}

export interface LabourAdvanceRequest {
  amount: number;
  date: string;
  note: string;
}

/** One row of a labour's advance ledger. All money fields are decimal STRINGS. */
export type AdvanceLedgerEntryType = 'advance_given' | 'advance_returned' | 'salary_deduction';

export interface AdvanceLedgerEntry {
  date: string; // YYYY-MM-DD
  entry_type: AdvanceLedgerEntryType;
  given: string;
  received_back: string;
  remarks: string;
  running_balance: string; // negative means the company owes the labour
}

export interface AdvanceLedger {
  labour_id: string;
  full_name: string;
  opening_balance: string;
  total_given: string;
  total_received_back: string;
  closing_balance: string;
  entries: AdvanceLedgerEntry[];
}

export interface SalaryDetails {
  labour_id: string;
  full_name: string;
  total_normal_hours: number;
  total_ot_hours: number;
  normal_hourly_rate: number;
  ot_hourly_rate: number;
  total_normal_pay: number;
  total_ot_pay: number;
  total_travel_allowance: number;
  /**
   * @deprecated No longer "advance given this period" — it now carries the outstanding
   * balance. Use {@link SalaryDetails.advance_outstanding}, which is the same value
   * under an unambiguous name.
   */
  total_advance_given: number;
  /** Outstanding before the period started. Decimal string. */
  opening_advance_balance?: string;
  /** Given during the period. Decimal string. */
  advance_given_in_period?: string;
  /** Handed back during the period, as a positive number. Decimal string. */
  advance_returned_in_period?: string;
  /** The true outstanding balance; negative means the company owes the labour. Decimal string. */
  advance_outstanding?: string;
  pending_salary: number;
  net_payable: number;

  // Editable fields
  advance_deducted?: number;
  paid_amount?: number;
  payment_mode?: 'Cash' | 'UPI';
  transaction_id?: string;
  message?: string;
}

export interface Company {
  id: string;
  name: string;
  code: string | null;
  address: string;
  description: string | null;
  email: string | null;
  contact_number: string | null;
  website: string | null;
  gst_number: string | null;
  created_at: string;
  updated_at: string;
}

export interface Factory {
  id: string;
  name: string;
  address: string;
  created_at: string;
  updated_at: string;
}

export interface FactoryStockItem {
  id: string;
  product_packaging: string;
  product_name: string;
  packaging_type: string;
  packing_size: number;
  unit: string;
  factory: string;
  factory_name: string;
  company_name: string;
  nos: number;
  units_available: string;
  boxes_available: string;
  created_at: string;
  updated_at: string;
  total_stock_cost: string;
}

export interface FactoryBatchStock {
  id: string;
  production_entry: string;
  production_number: string;
  batch_number: string;
  manufacturing_date: string | null;
  expiry_date: string | null;
  product_packaging: string;
  product_name: string;
  packaging_type: string;
  packing_size: number;
  unit: string;
  factory: string;
  factory_name: string;
  /** Units per box, from the product packaging. 0 or null when the packaging has none. */
  nos: number;
  units_available: string;
  boxes_available: string;
  total_stock_cost: string;
}

export interface WarehouseBatchStock {
  id: string;
  production_entry: string;
  production_number: string;
  batch_number: string;
  manufacturing_date: string | null;
  expiry_date: string | null;
  product_packaging: string;
  product_name: string;
  packaging_type: string;
  packing_size: number;
  unit: string;
  warehouse: string;
  warehouse_name: string;
  /** Units per box, from the product packaging. 0 or null when the packaging has none. */
  nos: number;
  units_available: string;
  boxes_available: string;
  total_stock_cost: string;
}

export interface ProductionStockItem {
  id: string;
  product_packaging: string;
  product_name: string;
  packaging_type: string;
  packing_size: number;
  unit: string;
  warehouse: string;
  warehouse_name: string;
  units_available: string;
  boxes_available: string;
  total_stock_cost: string;
  created_at: string;
  updated_at: string;
}

export interface InventoryStockItem {
  id: string;
  product_name: string;
  packaging_type: string;
  packing_size: number;
  unit: string;
  nos: number;
  warehouse_name: string | null;
  units_available: string;
  boxes_available: string;
  batch_number: string | null;
  manufacturing_date: string | null;
  expiry_date: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * One product batch on a transfer. A transfer carries any number of these; the header
 * fields (destination, vehicle, invoice) sit on the transfer itself.
 */
export interface ProductTransferItem {
  id: string;
  product_packaging: string;
  product: string;
  product_name: string;
  packaging_type: string | null;
  packing_size: string;
  unit: string | null;
  /** Ready-made "500 L / Bottle", already handling a null type or unit. Prefer it. */
  packaging_label: string;
  production_entry: string;
  batch_number: string;
  manufacturing_date: string | null;
  expiry_date: string | null;
  /** Units. `box_quantity` is the boxes those units came in. */
  quantity: string;
  box_quantity: number;
}

/** One consignment: one vehicle, one invoice, one destination, many product lines. */
export interface ProductTransfer {
  id: string;
  factory: string;
  factory_name: string;
  warehouse: string | null;
  warehouse_name: string | null;
  dealer: string | null;
  dealer_name: string | null;
  /** The warehouse name or the dealer's shop name, whichever the transfer went to. */
  destination_name: string;
  transfer_date: string;
  invoice_number: string | null;
  invoice_date: string | null;
  vehicle_number: string | null;
  driver_name: string | null;
  transferred_by: string | null;
  transferred_by_name: string | null;
  item_count: number;
  /** Units across every line. `total_box_quantity` is the boxes. */
  total_quantity: string;
  total_box_quantity: number;
  items: ProductTransferItem[];
  created_at: string;
  updated_at: string;
}

/**
 * The 400 body DELETE /product-transfers/{id}/ answers with when the destination
 * warehouse no longer holds the units. `level` ("batch" or "warehouse") says which
 * balance came up short and is for debugging, not for the user: show `detail`.
 * A transfer to a dealer cannot hit this, since dealers are not stock-tracked.
 */
export interface ProductTransferDeleteError {
  error: string;
  detail: string;
  level?: 'batch' | 'warehouse';
  available?: number;
  requested?: number;
  shortage?: number;
}

/** Which go-down an adjustment is against. Drives the endpoint, reasons and labels. */
export type StockAdjustmentScope = 'factory' | 'warehouse';

export enum FactoryStockAdjustmentReason {
  STOCK_TAKE = 'stock_take',
  PRODUCTION_MISCOUNT = 'production_miscount',
  DAMAGE_OR_EXPIRY = 'damage_or_expiry',
  SAMPLE_OR_TESTING = 'sample_or_testing',
  CORRECTION = 'correction',
  OTHER = 'other',
}

export enum WarehouseStockAdjustmentReason {
  STOCK_TAKE = 'stock_take',
  MISSING_TRANSFER = 'missing_transfer',
  DAMAGE_OR_EXPIRY = 'damage_or_expiry',
  SAMPLE_OR_TESTING = 'sample_or_testing',
  RETURN_FROM_DEALER = 'return_from_dealer',
  CORRECTION = 'correction',
  OTHER = 'other',
}

/** The code either endpoint accepts. Which codes are valid depends on the scope. */
export type StockAdjustmentReason = FactoryStockAdjustmentReason | WarehouseStockAdjustmentReason;

/** The one code both endpoints reject without remarks. */
export const STOCK_ADJUSTMENT_REASON_OTHER = 'other';

/**
 * Our own labels. The API's `reason_display` is title-cased straight from the code
 * ("Damage Or Expiry"), so prefer these everywhere a reason is shown to a user.
 *
 * The two lists are deliberately separate: a warehouse receives transfers and takes
 * dealer returns, a factory produces. Sharing one would offer reasons the endpoint
 * rejects.
 */
export const FACTORY_STOCK_ADJUSTMENT_REASON_LABELS: Record<FactoryStockAdjustmentReason, string> = {
  [FactoryStockAdjustmentReason.STOCK_TAKE]: 'Physical stock count',
  [FactoryStockAdjustmentReason.PRODUCTION_MISCOUNT]: 'Production miscount',
  [FactoryStockAdjustmentReason.DAMAGE_OR_EXPIRY]: 'Damaged or expired',
  [FactoryStockAdjustmentReason.SAMPLE_OR_TESTING]: 'Sample or testing',
  [FactoryStockAdjustmentReason.CORRECTION]: 'Data correction',
  [FactoryStockAdjustmentReason.OTHER]: 'Other (remarks required)',
};

export const WAREHOUSE_STOCK_ADJUSTMENT_REASON_LABELS: Record<WarehouseStockAdjustmentReason, string> = {
  [WarehouseStockAdjustmentReason.STOCK_TAKE]: 'Physical stock count',
  [WarehouseStockAdjustmentReason.MISSING_TRANSFER]: 'Arrived but never keyed in',
  [WarehouseStockAdjustmentReason.DAMAGE_OR_EXPIRY]: 'Damaged or expired',
  [WarehouseStockAdjustmentReason.SAMPLE_OR_TESTING]: 'Sample or testing',
  [WarehouseStockAdjustmentReason.RETURN_FROM_DEALER]: 'Returned by a dealer',
  [WarehouseStockAdjustmentReason.CORRECTION]: 'Data correction',
  [WarehouseStockAdjustmentReason.OTHER]: 'Other (remarks required)',
};

export interface StockAdjustmentReasonOption {
  value: StockAdjustmentReason;
  label: string;
}

function toReasonOptions(labels: Record<string, string>): StockAdjustmentReasonOption[] {
  return Object.keys(labels).map(value => ({ value: value as StockAdjustmentReason, label: labels[value] }));
}

export const FACTORY_STOCK_ADJUSTMENT_REASON_OPTIONS = toReasonOptions(FACTORY_STOCK_ADJUSTMENT_REASON_LABELS);
export const WAREHOUSE_STOCK_ADJUSTMENT_REASON_OPTIONS = toReasonOptions(WAREHOUSE_STOCK_ADJUSTMENT_REASON_LABELS);

export function stockAdjustmentReasonOptions(scope: StockAdjustmentScope): StockAdjustmentReasonOption[] {
  return scope === 'factory' ? FACTORY_STOCK_ADJUSTMENT_REASON_OPTIONS : WAREHOUSE_STOCK_ADJUSTMENT_REASON_OPTIONS;
}

export function stockAdjustmentReasonLabel(scope: StockAdjustmentScope, reason: StockAdjustmentReason): string {
  const labels: Record<string, string> =
    scope === 'factory' ? FACTORY_STOCK_ADJUSTMENT_REASON_LABELS : WAREHOUSE_STOCK_ADJUSTMENT_REASON_LABELS;

  return labels[reason] ?? '';
}

export enum StockAdjustmentDirection {
  ADD = 'add',
  REMOVE = 'remove',
}

/**
 * Both endpoints' rows normalised to one shape. They differ only in whether the second
 * balance is keyed `factory_*` or `warehouse_*`; the service maps that on the way in so
 * nothing downstream has to branch. Every decimal arrives from DRF as a string.
 */
export interface ProductStockAdjustment {
  id: string;
  production_entry: string;
  production_number: string;
  batch_number: string;
  product_name: string;
  packaging_type: string | null;
  /** The factory or warehouse id, depending on scope. */
  container: string;
  container_name: string | null;
  quantity: string; // signed, in units: positive adds stock, negative removes it
  boxes: string; // the same signed quantity in boxes, or "-" when the packaging has no nos
  reason: StockAdjustmentReason;
  reason_display: string;
  remarks: string | null;
  batch_units_available_after: string;
  /** `factory_units_available_after` or `warehouse_units_available_after`. */
  container_units_available_after: string;
  created_by: string;
  created_by_name: string;
  created_at: string;
}

/**
 * `quantity` is signed and in UNITS, never boxes. `warehouse` is required on the
 * warehouse endpoint and must be absent on the factory one, where the factory is derived
 * from the batch and anything sent is ignored.
 */
export interface StockAdjustmentRequest {
  production_entry: string;
  warehouse?: string;
  quantity: string;
  reason: StockAdjustmentReason;
  remarks?: string;
}

export interface StockAdjustmentFilters {
  production_entry?: string | null;
  factory?: string | null;
  warehouse?: string | null;
  product?: string | null;
  product_packaging?: string | null;
  company?: string | null;
  reason?: StockAdjustmentReason | null;
  created_by?: string | null;
  direction?: StockAdjustmentDirection | null;
  from_date?: string | null;
  to_date?: string | null;
  search?: string | null;
  page?: number | null;
}

/**
 * The 400 returned when a removal exceeds the balance. Unlike the other 400s, its
 * `quantity` is a plain string rather than an array, and every number is a string too.
 * `level` ("batch", or "factory"/"warehouse") says which balance came up short:
 * debugging, not UI.
 */
export interface StockShortageError {
  quantity: string;
  level?: string;
  batch_number?: string;
  factory?: string;
  warehouse?: string;
  available: string;
  requested: string;
  shortage: string;
}

/** The batch an adjustment targets. Fixed: the form never picks the batch. */
export interface ProductStockAdjustmentContext {
  scope: StockAdjustmentScope;
  production_entry: string;
  batch_number: string;
  product_name: string;
  packaging_label: string;
  /** Units per box. 0 when the packaging has none, which forces the form into units. */
  nos: number;
  unit: string;
  /**
   * The factory or warehouse this adjustment lands on. Null only for a warehouse
   * adjustment opened from a batch-centric screen: one batch can sit in several
   * warehouses with different balances, so the form must ask rather than infer.
   */
  container_id: string | null;
  container_name: string | null;
  /** Offered when `container_id` is null. Warehouse scope only. */
  warehouseOptions?: { id: string; name: string }[];
  /** Omitted when the current balance is not to hand. */
  batch_units_available?: string | null;
  /** The product's aggregate in the same go-down, which moves by the same amount. */
  container_units_available?: string | null;
}

/** One line of a create body. The batch must belong to the packaging on the same line. */
export interface ProductTransferItemRequest {
  product_packaging: string;
  production_entry: string;
  /** Boxes, integer, at least 1. */
  box_quantity: number;
}

/**
 * The create body. Header fields go once, goods go in `items`, and at least one item is
 * required. There is no single-product form of this body: a top-level `product_packaging`,
 * `production_entry` or `box_quantity` is a 400.
 */
export interface ProductTransferRequest {
  factory: string;
  /** Exactly one of `warehouse` or `dealer`. Neither or both is a 400. */
  warehouse?: string | null;
  dealer?: string | null;
  transfer_date: string;
  invoice_number?: string | null;
  invoice_date?: string | null;
  vehicle_number?: string | null;
  driver_name?: string | null;
  items: ProductTransferItemRequest[];
}

/**
 * What a rejected line carries. The 400 body's `items` is an array as long as the one
 * posted, with `{}` for the lines that are fine, so the index is the row. A short line
 * rejects the whole transfer: nothing is created and no stock moves.
 */
export interface ProductTransferItemError {
  box_quantity?: string | string[];
  production_entry?: string | string[];
  product_packaging?: string | string[];
}

export interface BomConsumptionMaterial {
  raw_material: string;
  raw_material_name: string;
  raw_material_code: string;
  unit: string;
  quantity_required: number;
}

export interface BomConsumptionResponse {
  has_bom: boolean;
  materials: BomConsumptionMaterial[];
}

export interface ProductionEntryReqBody {
  factory: string;
  production_date: string | Date;
  entries: {
    product_packaging: string | number; // UUID or ID from backend
    box_produced: number;
    batch_number: string;
    remarks?: string;
    manufacturing_date?: string;
    expiry_date?: string;
  }[];
}

export interface RawMaterialConsumptionRequest {
  production_entry: string;
  raw_material: string;
  quantity_consumed: number;
  warehouse: string;
}

export interface InsufficientStockError {
  raw_material: string;
  available: number;
  required: number;
  shortage: number;
}

export interface RawMaterialConsumption {
  id: string;
  raw_material_name: string;
  raw_material_code: string;
  unit_name: string;
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  quantity_consumed: string;
  production_entry: string;
  raw_material: string;
  warehouse: string;
  warehouse_name: string;
}

export interface ProductionEntry {
  id: string;
  product_name: string;
  packaging_type: string;
  packing_size: number;
  packaging_unit: string;
  factory_name: string;
  factory: string;
  company_name: string;
  manufacturing_date: string | null;
  expiry_date: string | null;
  raw_material_consumptions: RawMaterialConsumption[];
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  production_number: string;
  box_produced: string;
  units: string;
  batch_number: string;
  production_date: string; // ISO date
  remarks: string;
  product_packaging: string;
  created_by: string;
}

/**
 * The 400 body DELETE /production-entries/{id}/ answers with when the entry cannot be
 * reversed. `error` names the blocker, `detail` is the sentence to show the admin; the
 * remaining fields are only present on the case that carries them. Nothing is written
 * on a 400, so the delete is safe to retry once the blocker is cleared.
 */
export interface ProductionEntryDeleteError {
  error: string;
  detail: string;
  transfers?: string[];
  available?: number;
  requested?: number;
  shortage?: number;
}

/** `error` on a delete blocked by transfers out of the factory. */
export const PRODUCTION_ENTRY_TRANSFERRED_ERROR = 'Production entry has been transferred';

export interface ProductionEntryInsight {
  product_id: string;
  product_name: string;
  packing_size: number;
  packaging_unit: string;
  warehouse_name: string;
  created_by_name: string;
  nos: number;
  box_produced: string;
  total_nos_produced: number;
  production_date: string;
  packaging_type: string;
}
